import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks";
import {
	BlockedContentType,
	SITE_VISIT_COMMENT_MIN_CHARS,
	countCommentChars,
	getBlockedContentType,
	isCpAppSource,
} from "../../utils/functions/site-visit-comment";
import AudienceNote from "./audience-note";
import GuidelinesContent from "./guidelines-content";
import {
	DIALOG_COLORS,
	DIALOG_FONT_FAMILY,
	dialogFooterSx,
	dialogHeaderSx,
	dialogPaperSx,
	footerButtonSx,
} from "./styles";

const GUIDELINES_EXPANDED_KEY = "siteVisitCommentGuidelinesExpanded";
const BLOCKED_MESSAGE_DELAY_MS = 400;

export interface CheckoutCommentDetails {
	customerName?: string;
	project?: string;
	opportunityId?: string;
	source?: string;
	cpName?: string;
	checkInTime?: string | null;
}

interface CheckoutCommentDialogProps {
	open: boolean;
	// Cancel, the close icon, Esc and a backdrop click all end up here. The salesperson stays checked in.
	onClose: () => void;
	comment: string;
	onCommentChange: (comment: string) => void;
	onSubmit: () => void;
	submitting?: boolean;
	details: CheckoutCommentDetails;
}

const readGuidelinesExpanded = (storageKey: string) => {
	try {
		return localStorage.getItem(storageKey) === "true";
	} catch {
		return false;
	}
};

const chipSx = {
	fontFamily: DIALOG_FONT_FAMILY,
	fontSize: 13,
	height: 28,
	borderRadius: "6px",
	backgroundColor: DIALOG_COLORS.surface,
};

const CheckoutCommentDialog = ({
	open,
	onClose,
	comment,
	onCommentChange,
	onSubmit,
	submitting = false,
	details,
}: CheckoutCommentDialogProps) => {
	const { t } = useTranslation();
	const userDetails = useAppSelector((state) => state.user.userDetails) as { email?: string; name?: string };
	// Remembered per user, so two people sharing a browser keep their own setting
	const storageKey = `${GUIDELINES_EXPANDED_KEY}:${userDetails?.email ?? userDetails?.name ?? "user"}`;
	const [guidelinesExpanded, setGuidelinesExpanded] = useState(() => readGuidelinesExpanded(storageKey));
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [shownBlockedType, setShownBlockedType] = useState<BlockedContentType | null>(null);

	const isCp = isCpAppSource(details.source);
	const charCount = countCommentChars(comment);
	const charsRemaining = Math.max(SITE_VISIT_COMMENT_MIN_CHARS - charCount, 0);
	const isMinMet = charsRemaining === 0;
	// Submit is blocked right away; only the message under the box waits for the typing to pause
	const blockedType = isCp ? getBlockedContentType(comment) : null;
	const isSubmitDisabled = !isMinMet || Boolean(blockedType) || submitting;

	useEffect(() => {
		setGuidelinesExpanded(readGuidelinesExpanded(storageKey));
	}, [storageKey]);

	useEffect(() => {
		if (open) setSubmitAttempted(false);
	}, [open]);

	useEffect(() => {
		if (!blockedType) {
			setShownBlockedType(null);
			return;
		}
		const timer = setTimeout(() => setShownBlockedType(blockedType), BLOCKED_MESSAGE_DELAY_MS);
		return () => clearTimeout(timer);
	}, [comment, blockedType]);

	const toggleGuidelines = () => {
		const next = !guidelinesExpanded;
		setGuidelinesExpanded(next);
		try {
			localStorage.setItem(storageKey, String(next));
		} catch {
			// private mode / blocked storage: the panel just won't be remembered
		}
	};

	const subtitle = [details.customerName, details.project, details.opportunityId].filter(Boolean).join(" · ");

	const counterColor = isMinMet
		? DIALOG_COLORS.doHeading
		: submitAttempted
		? DIALOG_COLORS.dontHeading
		: DIALOG_COLORS.muted;

	const formatCheckIn = (time: string) =>
		moment(time).isSame(moment(), "day") ? moment(time).format("hh:mm A") : moment(time).format("DD MMM, hh:mm A");

	const renderChips = () => (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
			{details.checkInTime && moment(details.checkInTime).isValid() && (
				<Chip
					sx={chipSx}
					label={t("site_visit_comment.dialog.chip_check_in", { time: formatCheckIn(details.checkInTime) })}
				/>
			)}
			{isCp && details.cpName ? (
				<Chip
					sx={chipSx}
					label={
						<>
							{t("site_visit_comment.dialog.chip_cp")} <b>{details.cpName}</b>
						</>
					}
				/>
			) : (
				details.source && (
					<Chip
						sx={chipSx}
						label={
							<>
								{t("site_visit_comment.dialog.chip_source")} <b>{details.source}</b>
							</>
						}
					/>
				)
			)}
		</Box>
	);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={dialogPaperSx}>
			<DialogTitle component="div" sx={dialogHeaderSx}>
				<Typography component="h2" sx={{ fontFamily: DIALOG_FONT_FAMILY, fontSize: 22, fontWeight: 700 }}>
					{t("site_visit_comment.dialog.title")}
				</Typography>
				{subtitle && (
					<Typography sx={{ fontFamily: DIALOG_FONT_FAMILY, fontSize: 14, color: DIALOG_COLORS.muted, mt: 0.5 }}>
						{subtitle}
					</Typography>
				)}
				{renderChips()}
				<IconButton
					aria-label={t("site_visit_comment.dialog.close")}
					onClick={onClose}
					sx={{ position: "absolute", right: 16, top: 20, color: DIALOG_COLORS.muted }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ px: 3.5, pt: 3, pb: 3 }}>
				<Typography
					component="label"
					htmlFor="site-visit-comment-input"
					sx={{ display: "block", fontFamily: DIALOG_FONT_FAMILY, fontSize: 13, color: DIALOG_COLORS.muted, mb: 0.75, mt: 1 }}
				>
					{t("site_visit_comment.dialog.comment_label")} <span style={{ color: DIALOG_COLORS.required }}>*</span>
				</Typography>
				<TextField
					id="site-visit-comment-input"
					fullWidth
					multiline
					minRows={3}
					autoFocus
					value={comment}
					onChange={(e) => onCommentChange(e.target.value)}
					placeholder={t("site_visit_comment.dialog.placeholder")}
					sx={{
						"& .MuiOutlinedInput-root": {
							fontFamily: DIALOG_FONT_FAMILY,
							fontSize: 14,
							borderRadius: "6px",
							"& fieldset": { borderColor: DIALOG_COLORS.border },
							"&:hover fieldset": { borderColor: DIALOG_COLORS.muted },
							"&.Mui-focused fieldset": { borderColor: DIALOG_COLORS.link, borderWidth: 1 },
						},
					}}
				/>
				<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.75 }}>
					<Typography
						sx={{
							fontFamily: DIALOG_FONT_FAMILY,
							fontSize: 13,
							color: shownBlockedType ? DIALOG_COLORS.dontHeading : DIALOG_COLORS.muted,
						}}
					>
						{shownBlockedType
							? t(`site_visit_comment.dialog.blocked_${shownBlockedType}`)
							: t("site_visit_comment.dialog.helper")}
					</Typography>
					<Typography sx={{ fontFamily: DIALOG_FONT_FAMILY, fontSize: 13, color: counterColor, whiteSpace: "nowrap" }}>
						{t("site_visit_comment.dialog.counter", { count: charCount, min: SITE_VISIT_COMMENT_MIN_CHARS })}
					</Typography>
				</Box>

				{isCp && <AudienceNote />}
				<GuidelinesContent isCp={isCp} expanded={guidelinesExpanded} onToggle={toggleGuidelines} />
			</DialogContent>

			<DialogActions sx={dialogFooterSx}>
				{!isMinMet && !submitting && (
					<Typography sx={{ flex: 1, fontFamily: DIALOG_FONT_FAMILY, fontSize: 13, color: DIALOG_COLORS.muted }}>
						{t("site_visit_comment.dialog.still_needed", { count: charsRemaining })}
					</Typography>
				)}
				<Button
					onClick={onClose}
					variant="outlined"
					sx={{ ...footerButtonSx, color: DIALOG_COLORS.text, borderColor: DIALOG_COLORS.border }}
				>
					{t("site_visit_comment.dialog.cancel")}
				</Button>
				{/* A disabled button swallows clicks, so the wrapper is what notices a too-early press */}
				<span onClick={() => isSubmitDisabled && setSubmitAttempted(true)}>
					<Button
						onClick={onSubmit}
						disabled={isSubmitDisabled}
						variant="contained"
						disableElevation
						sx={{
							...footerButtonSx,
							backgroundColor: "#04AA6D",
							color: "white",
							"&:hover": { backgroundColor: "#039a62" },
							"&.Mui-disabled": { backgroundColor: "#d3d6db", color: "#8a8f98", cursor: "not-allowed" },
						}}
					>
						{submitting ? (
							<>
								{t("site_visit_comment.dialog.submitting")}{" "}
								<CircularProgress color="inherit" size={18} sx={{ ml: 1 }} />
							</>
						) : (
							t("site_visit_comment.dialog.submit")
						)}
					</Button>
				</span>
			</DialogActions>
		</Dialog>
	);
};

export default CheckoutCommentDialog;
